import { afterNextRender, afterRenderEffect, Component, computed, inject, OnInit, Type, viewChild, ViewContainerRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router, UrlSegment } from '@angular/router';
import { BillingClient, CustomerSurveyClient } from 'api-clients/agency-admin-api-client';
import { AccountClient as NotificationAccountClient } from 'api-clients/notification-api-client';
import { CitizensEulaClient, UWRequestClient } from 'api-clients/rating-api-client';
import { StatusType, TwilioA2PRegistrationClient } from 'api-clients/voice-and-text-api-client';
import dayjs from 'dayjs';
import { lastValueFrom } from 'rxjs';
import { AccountContextService } from 'src/shared/account/account-context.service';
import { AppLoadingService } from 'src/shared/app-loading.service';
import { CitizensEulaService } from 'src/shared/citizens-eula/citizens-eula.service';
import { SidePanelService } from 'src/shared/ez-side-panel/side-panel.service';
import { LayoutBaseComponent } from 'src/shared/layout-base/layout-base.component';
import { MobileStateService } from 'src/shared/mobile-state.service';
import { PolicyContextService } from 'src/shared/policy/policy-context.service';
import { ResizableSidePanelSettingsEvent } from 'src/shared/resizable-sidenav/resizable-side-panel-settings';
import { globalWindow } from 'src/shared/window-globals';
import { NavMenuService } from '../shared/nav-menu.service';
import { UserService } from '../shared/user.service';
import { AcordEulaModalComponent } from './acord-eula/acord-eula-modal.component';
import { noShellRootPaths } from './app.routing';
import { DashboardDataService } from './dashboard/services/dashboard-data.service';
import { FiveComponentRefService } from './five-component-ref.service';
import { PastDueModalComponent } from './past-due-modal/past-due-modal.component';
import { SurveyComponent } from './survey/survey.component';
import { TwilioRegistrationModalComponent } from './twilio-registration-modal/twilio-registration-modal.component';
import { WhatsNewModalComponent } from './whats-new/whats-new-modal.component';
import { WorkspaceService } from './workspace/workspace.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    DashboardDataService,
    CitizensEulaService,
    { provide: BillingClient, useValue: new BillingClient() },
    { provide: CitizensEulaClient, useValue: new CitizensEulaClient() },
    { provide: UWRequestClient, useValue: new UWRequestClient() },
  ],
  standalone: false,
})
export class AppComponent extends LayoutBaseComponent implements OnInit {

  readonly mainSidePanelViewContainerRef = viewChild('mainSideNavContainer', { read: ViewContainerRef });
  protected readonly extendedWorkspaceTemplate = viewChild('extendedWorkspaceTemplate', { read: ViewContainerRef });

  mobileMenuVisible = false;
  isMobile = false;
  hideShell: boolean;
  #twilioDialogSessionStorageKey = 'twilio:DialogDisplayed';
  readonly #whatsNewLocalStorageTag = 'WhatsNew:User';
  readonly acordEulaExpirationDays = 90;
  readonly #acordEulaLocalStorageTag = 'AcordEula:User';
  #singleDialogRef: MatDialogRef<unknown>; //  any reason to provide unioned dialog types
  #orderedDialogs: (() => Promise<void>)[];

  readonly router = inject(Router);
  protected readonly userService = inject(UserService);
  protected readonly navMenuService = inject(NavMenuService);
  readonly #workspaceService = inject(WorkspaceService);
  readonly appLoadingService = inject(AppLoadingService);
  readonly #fiveComponentRef = inject(FiveComponentRefService);
  readonly #mobileState = inject(MobileStateService);
  readonly #twilioA2PRegistrationClient = inject(TwilioA2PRegistrationClient);
  readonly #customerSurveyClient = inject(CustomerSurveyClient);
  readonly #dialog = inject(MatDialog);
  #sanitizer = inject(DomSanitizer);
  #ratingDataService = inject(DashboardDataService);
  #billingClient = inject(BillingClient);
  readonly #citizensEulaService = inject(CitizensEulaService);
  readonly #notificationAccountClient = inject(NotificationAccountClient);
  readonly #sidePanelService = inject(SidePanelService);
  readonly isMainSidePanelOpen = this.#sidePanelService.isOpen;
  readonly mainSidePanelConfig = this.#sidePanelService.config;
  readonly mainSidePanelMaxWidth = computed(() => this.mainSidePanelConfig().maximumWidth ? this.mainSidePanelConfig().maximumWidth : '90%');
  readonly mainSidePanelMinWidth = computed(() => this.mainSidePanelConfig().minimumWidth ? this.mainSidePanelConfig().minimumWidth : '200px');
  readonly #accountContextService = inject(AccountContextService);
  readonly #policyContextService = inject(PolicyContextService);

  protected readonly extendedWorkspaceComponent = this.#workspaceService.extendedWorkspaceComponent.asReadonly();
  protected readonly showExtendedWorkspace = computed(() => !!this.extendedWorkspaceComponent());

  constructor() {
    super();
    afterNextRender({ read: () => this.#sidePanelService.setViewContainer(this.mainSidePanelViewContainerRef()) });
    this.#mobileState.windowResize.subscribe(isMobile => this.isMobile = isMobile);

    this.hideShell = noShellRootPaths.some(rootPath => rootPath.test(window.location.pathname));

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const extractedUrl = this.router.getCurrentNavigation().extractedUrl;
        const segments: UrlSegment[] = extractedUrl?.root.children.primary?.segments || [];
        if (extractedUrl && segments.length && (segments[0].path !== 'account' || !segments[1].path.match(/^\d+$/))) {
          this.#accountContextService.updateApplicantContext(null);
        }
        if (extractedUrl && !this.#isPolicyUrl(segments)) {
          this.#policyContextService.clearPolicyContext();
        }
      }

      if (event instanceof NavigationEnd) {
        this.hideShell = noShellRootPaths.some(rootPath => rootPath.test(window.location.pathname));
        // TODO remove when ws4 is ported to angular or when ws5 is GA
        const origin = `${window.location.protocol}//${window.location.host}`;
        window.postMessage('accountLoaded', origin);
      }
    });

    this.#fiveComponentRef.setFiveComponentRef();

    // the order of these dialogs was chosen by product. If you need to add/remove/reorder please reach out to them first
    this.#orderedDialogs = [
      this.#getSurvey,
      this.#getBillingDialog,
      this.#checkAcordEula,
      this.#citizensEulaService.checkCitizensEulaOnLogin,
      this.#checkTwilioRegistrationStatus,
      this.#checkWhatsNew,
    ];

    this.#trackExtendedWorkspaceTemplate();
  }

  async ngOnInit(): Promise<void> {
    this.checkChromeExtension();
    this.#loadLegacyModalandIconCSS();
    this.#sidePanelService.setViewContainer(this.mainSidePanelViewContainerRef()!);

    for (const dialog of this.#orderedDialogs) {
      if (this.#singleDialogRef) return;

      await dialog();
    }
  }

  #isPolicyUrl(segments: UrlSegment[]): boolean {
    return segments[0]?.path === 'account'
      && (segments[2]?.path === 'policies' || segments[2]?.path === 'policy')
      && !!segments[3]?.path.match(/^\d+$/);
  }

  #trackExtendedWorkspaceTemplate(): void {
    // use afterRenderEffect to ensure that the ViewContainerRef doesn't disappear before the side-panel closes
    afterRenderEffect({
      read: () => {
        this.extendedWorkspaceTemplate()?.clear();
        if (this.extendedWorkspaceTemplate() == null || this.extendedWorkspaceComponent() == null) {
          return;
        }
        this.extendedWorkspaceTemplate().createComponent(this.extendedWorkspaceComponent() as Type<unknown>);
      },
    });
  }

  workspaceVisible(): boolean {
    return this.user && this.#workspaceService.showWorkspace;
  }

  closeWorkspace(): void {
    this.#workspaceService.close();
  }

  #loadLegacyModalandIconCSS = (): void => {
    // load css resources for jquery modals and any old ezawesome icons.
    // We intend to replace/remove these from Five and then we can remove this references as well
    const modalCSS = document.createElement('link');
    modalCSS.rel = 'stylesheet';
    modalCSS.href = globalWindow.UrlResolver.CDN('Shared/EzLynx4/styles/ezlynx/modal.css');

    const iconCSS = document.createElement('link');
    iconCSS.rel = 'stylesheet';
    iconCSS.href = globalWindow.UrlResolver.CDN('Shared/EzLynx4/styles/ezlynx/icons.css');

    document.head.append(modalCSS, iconCSS);
  };

  #checkTwilioRegistrationStatus = async (): Promise<void> => {
    const twilioDialogDisplayed = sessionStorage.getItem(this.#twilioDialogSessionStorageKey);
    if (!twilioDialogDisplayed || twilioDialogDisplayed === 'false') {

      const registrationDetail = await this.#twilioA2PRegistrationClient.getRegistrationStatus();
      sessionStorage.setItem(this.#twilioDialogSessionStorageKey, 'true');
      if (!registrationDetail) return;

      const status = StatusType[registrationDetail.status] as unknown as StatusType;

      if (status === StatusType.Unknown || status === StatusType.Draft || status === StatusType.TextingProductDisabled
        || status === StatusType.BrandStatusFailed) {
        this.#singleDialogRef = this.#dialog.open(TwilioRegistrationModalComponent, {
          data: registrationDetail,
          width: '600px',
        });
      }
    }
  };

  #getSurvey = async (): Promise<void> => {
    const surveyURL = await this.#customerSurveyClient.currentSurvey();
    if (surveyURL && localStorage.surveyDismissed !== surveyURL) {
      this.#singleDialogRef = this.#dialog.open(SurveyComponent, { data: surveyURL });
    }
  };

  #getBillingDialog = async (): Promise<void> => {
    const billingCurrent = await this.#billingClient.billingCurrentCheck();
    if (!billingCurrent) {
      this.#singleDialogRef = this.#dialog.open(PastDueModalComponent, { width: '540px' });
    }
  };

  #checkAcordEula = async (): Promise<void> => {
    // Check to see if we've shown this in 90 days, if so, don't show it again
    // Check local storage cache to avoid API call
    const acordEulaDateAccepted = localStorage.getItem(this.#acordEulaLocalStorageTag);
    const eulaDismissedByLoginAs = sessionStorage.getItem('EULADismissedByLoginAs');
    if (eulaDismissedByLoginAs) return; // short circuit immediately if the login as session has dismissed the EULA previously
    if (acordEulaDateAccepted) {
      const lastCheckedDate = dayjs(acordEulaDateAccepted);
      if (lastCheckedDate.isValid() && dayjs().startOf('day').diff(lastCheckedDate, 'day') < this.acordEulaExpirationDays) {
        return;
      }
    }

    // API check if ACORD EULA required
    const response = await this.#ratingDataService.getAcordEula();

    if (!response.required) return;

    if (!response.accepted && !eulaDismissedByLoginAs) {
      this.#singleDialogRef = this.#dialog.open(AcordEulaModalComponent, {
        disableClose: true,
      });
      const eulaAccepted = await lastValueFrom(this.#singleDialogRef.afterClosed());

      if (eulaAccepted) {
        localStorage.setItem(this.#acordEulaLocalStorageTag, dayjs().toISOString());
        this.#ratingDataService.acceptAcordEula();
      } else {
        sessionStorage.setItem('EULADismissedByLoginAs', 'true');
      }
    } else {
      localStorage.setItem(this.#acordEulaLocalStorageTag, dayjs().toISOString());
    }
  };

  #checkWhatsNew = async (): Promise<void> => {
    // Check to see if we've shown this today, if so, don't show it again
    const displayedWhatsNew = localStorage.getItem(this.#whatsNewLocalStorageTag);
    const differenceInDays = Math.abs(dayjs().startOf('day').diff(dayjs(displayedWhatsNew), 'day'));

    if (displayedWhatsNew && differenceInDays < 1) {
      return;
    }

    const whatsNewData = await this.#notificationAccountClient.getWhatsNew();
    if (whatsNewData) {
      if (whatsNewData.expires && dayjs().isAfter(dayjs(whatsNewData.expires))) {
        return;
      }

      const url = this.#sanitizer.bypassSecurityTrustResourceUrl(whatsNewData.bodyUrl);

      this.#singleDialogRef = this.#dialog.open(WhatsNewModalComponent, {
        disableClose: true,
        data: { contentUrl: url, title: whatsNewData.title, width: whatsNewData.width, height: whatsNewData.height },
      });

      const whatsNewId = whatsNewData.whatsNewId;
      const response = await lastValueFrom(this.#singleDialogRef.afterClosed());

      localStorage.setItem(this.#whatsNewLocalStorageTag, new Date().toISOString());
      if (response.acknowledge) {
        this.#notificationAccountClient.acknowledgeWhatsNew(whatsNewId);
      }
    }
  };

  protected mainSidePanelSettingsChanged(event: ResizableSidePanelSettingsEvent): void {
    this.#sidePanelService.onSettingsChanged(event.settings);
  }

  protected closeSidePanel(): void {
    this.#sidePanelService.closeSidePanel({ sender: this });
  }
}
